import React from 'react';
import {ChartsGraphs} from '@deere/ux.uxframe-react/lib/components/ChartsGraphs';
import {Box} from "@deere/fuel-react";

const distinct = (array) => {
    if (!array || array.length === 0) return [];
    const seen = new Set();
    const result = [];

    for (const item of array) {
        const key = item.x;
        if (!seen.has(key)) {
            seen.add(key);
            result.push(item);
        } else {
            const existingItem = result.find(r => r.x === item.x);
            if (existingItem && item.y > existingItem.y) {
                existingItem.y = item.y;
            }
        }
    }
    return result;
};

// Prepare data in the correct format for ChartsGraphs
const chartData = (axes, datapoint, xLabel, yLabel) => {
    return {
        labels: axes.map(p => p.x),
        datasets: [
            {
                label: xLabel,
                data: [datapoint],
                borderColor: '#ecdb69',
                backgroundColor: '#ECDB69FF',
                borderWidth: 0,
                pointBorderColor: '#ECDB69FF',
                pointBackgroundColor: '#ECDB69FF',
                pointHoverRadius: 12,
                pointRadius: 6,
                pointStyle: 'rectRot',
                showLine: false
            },
            {
                label: yLabel,
                data: axes,
                borderColor: '#367C2B',
                backgroundColor: 'rgba(256, 256, 256, 0)',
                borderWidth: 2,
                pointBorderColor: '#367C2B',
                pointBackgroundColor: 'rgba(256, 256, 256, 0)',
                pointHoverRadius: 4,
                pointRadius: 4,
                steppedLine: 'after',
                tension: 0,
                spanGaps: true
            }
        ]
    }
};

const qppmChartData = (axes, datapoint, xLabel, yLabel) => {
    return {
        labels: axes.map(p => p.x),
        datasets: [
            {
                label: xLabel,
                data: [datapoint],
                borderColor: '#ecdb69',
                backgroundColor: '#ECDB69FF',
                borderWidth: 0,
                pointBorderColor: '#ECDB69FF',
                pointBackgroundColor: '#ECDB69FF',
                pointHoverRadius: 12,
                pointRadius: 6,
                pointStyle: 'rectRot',
                showLine: false
            },
            {
                label: yLabel,
                data: axes,
                borderColor: '#367C2B',
                backgroundColor: 'rgba(256, 256, 256, 0)',
                borderWidth: 2,
                pointBorderColor: '#367C2B',
                pointBackgroundColor: 'rgba(256, 256, 256, 0)',
                pointHoverRadius: 4,
                pointRadius: 4,
                steppedLine: 'after',
                tension: 0,
                spanGaps: true
            },
            {
                label: yLabel,
                data: [axes[0], axes[1]],
                borderColor: '#367C2B',
                backgroundColor: 'rgba(54, 124, 43, 0.2)',
                borderWidth: 2,
                pointBorderColor: '#367C2B',
                pointBackgroundColor: 'rgba(256, 256, 256, 0)',
                pointHoverRadius: 4,
                pointRadius: 4,
                steppedLine: 'after',
                tension: 0,
                spanGaps: true,
                fill: 'start'
            },
            {
                label: yLabel,
                data: [axes[axes.length - 2], axes[axes.length - 1]],
                borderColor: '#367C2B',
                backgroundColor: 'rgba(220, 53, 69, 0.2)',
                borderWidth: 2,
                pointBorderColor: '#367C2B',
                pointBackgroundColor: 'rgba(256, 256, 256, 0)',
                pointHoverRadius: 4,
                pointRadius: 4,
                steppedLine: 'after',
                tension: 0,
                spanGaps: true,
                fill: 'end'
            },
        ]
    }
};


const chartOptions = (title, datapoint, axes, isPointRange, xLabel, yLabel) => {
    return {
        title: {
            display: true,
            text: title,
            font: {
                size: 18
            },
            color: '#367C2B'
        },
        legend: {
            display: false,
        },
        animation: {
            duration: 0
        },
        scales: {
            xAxes: [{
                position: 'bottom',
                scaleLabel: {
                    display: true,
                    labelString: xLabel,
                }
            }],
            yAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: yLabel,
                }
            }]
        },
        tooltips: {
            intersect: false,
            axis: 'x',
            callbacks: {
                label: (tooltipItem, data) => {
                    if (tooltipItem.datasetIndex === 0) {
                        return datapoint.label;
                    }
                    if (tooltipItem.datasetIndex > 1) {
                        return "";
                    }
                    if (isPointRange) {
                        return `${axes[Math.max(tooltipItem.index - 1, 0)].x} - ${tooltipItem.label} = ${tooltipItem.value} ${yLabel}`;
                    }
                    return `${tooltipItem.label} = ${tooltipItem.value} ${yLabel}`;
                },
                title: (tooltipItem, data) => ""
            }
        },

    }
};

export default function ReactChart({axes, datapoint, title, xLabel, yLabel}) {
    const isPointRange = (axes) => {
        if (!axes || axes?.length < 2) return false;
        for (let i = 0; i < axes.length - 1; i++) {
            if (axes[i + 1].x - axes[i].x !== 1 && axes[i + 1].x - axes[i].x !== 0) {
                return true;
            }
        }
        return false;
    };
    const filteredAxes = distinct(axes);
    const isQppmData = () => {
        return title.includes("QPPM") || xLabel.includes("QPPM");
    }
    return (
        <Box width={'100%'}>
            <ChartsGraphs
                type="line"
                data={isQppmData() ? qppmChartData(filteredAxes, datapoint, xLabel, yLabel)
                    : chartData(filteredAxes, datapoint, title, xLabel, yLabel)}
                options={chartOptions(title, datapoint, filteredAxes, isPointRange(filteredAxes), xLabel, yLabel)}
                height={400}
            />
        </Box>
    );
};
